import React, { useMemo, useState } from 'react';
import axios from 'axios';

import { ConfirmModal } from '~/components/ConfirmModal';
import Button from '~/components/core/Atomic/Buttons/Button';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import { AddIcon } from '~/components/icons';
import OperationsBreadcrumbs from '~/components/SystemConfiguration/OperationsBreadcrumbs';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { OrganizationModel } from '~/components/types/organization-types';
import useDataFetcher from '~/components/useDataFetcher';
import { reportAxiosError } from '~/Utils';

import PredefinedPromptDialog from './PredefinedPromptDialog';
import PredefinedPromptsTable from './PredefinedPromptsTable';
import TEXTS from './texts';
import type { PredefinedPromptFormValues, PredefinedPromptRow, PredefinedPrompts } from './types';

const PredefinedPromptsTab: React.FC = () => {
  const { organization } = useSysconfig() as { organization: OrganizationModel };
  // The PUT replaces the organization's whole prompt map.
  const promptsUrl = `/sysconfig/api/v1/organizations/${organization.id}/ai_configuration/predefined_prompts`;

  const { data, isLoading, isError, reloadData } = useDataFetcher<PredefinedPrompts>(promptsUrl);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState<PredefinedPromptRow | null>(null);
  const [promptToDelete, setPromptToDelete] = useState<PredefinedPromptRow | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const prompts = useMemo(() => data ?? {}, [data]);
  const rows = useMemo<PredefinedPromptRow[]>(
    () => Object.entries(prompts).map(([promptKey, prompt]) => ({ id: promptKey, prompt_key: promptKey, ...prompt })),
    [prompts]
  );

  const savePrompts = async (nextPrompts: PredefinedPrompts): Promise<boolean> => {
    setIsSaving(true);
    try {
      await axios.put(promptsUrl, nextPrompts);
      await reloadData();
      return true;
    } catch (error) {
      reportAxiosError(error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const openCreate = () => {
    setEditedPrompt(null);
    setIsDialogOpen(true);
  };

  const openEdit = (row: PredefinedPromptRow) => {
    setEditedPrompt(row);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditedPrompt(null);
  };

  const handleSubmit = async (values: PredefinedPromptFormValues) => {
    const promptKey = editedPrompt?.prompt_key ?? values.prompt_key;
    const isSaved = await savePrompts({
      ...prompts,
      [promptKey]: { prompt_title: values.prompt_title.trim(), prompt_text: values.prompt_text },
    });
    if (isSaved) closeDialog();
  };

  const handleConfirmDelete = async () => {
    if (!promptToDelete) return;
    const remainingPrompts = { ...prompts };
    delete remainingPrompts[promptToDelete.prompt_key];
    const isSaved = await savePrompts(remainingPrompts);
    if (isSaved) setPromptToDelete(null);
  };

  return (
    <div className="flex min-h-[50vh] flex-col gap-16 bg-white p-24">
      <OperationsBreadcrumbs currentTab={TEXTS.tabTitle} />
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-4">
          <Text variant={Text.VARIANTS.XL} weight={Text.WEIGHTS.SEMI_BOLD}>
            {TEXTS.tabTitle}
          </Text>
          <Text variant={Text.VARIANTS.SM}>{TEXTS.tabSubtitle}</Text>
        </div>
        <Button
          color="primary"
          startIcon={<AddIcon iconColor="currentColor" />}
          onClick={openCreate}
          disabled={isLoading || isError}
        >
          {TEXTS.addPrompt}
        </Button>
      </div>

      {isLoading || isError ? (
        <SkeletonTable rowsCount={3} columnsCount={5} isError={isError} />
      ) : (
        <PredefinedPromptsTable rows={rows} onEdit={openEdit} onDelete={setPromptToDelete} />
      )}

      {isDialogOpen && (
        <PredefinedPromptDialog
          key={editedPrompt?.prompt_key ?? '__create__'}
          editedPrompt={editedPrompt}
          existingKeys={Object.keys(prompts)}
          isSaving={isSaving}
          onSubmit={handleSubmit}
          onClose={closeDialog}
        />
      )}

      {promptToDelete && (
        <ConfirmModal
          isOpen
          isSubmitting={isSaving}
          title={TEXTS.deleteConfirm.title}
          contentText={TEXTS.deleteConfirm.body(promptToDelete.prompt_title || promptToDelete.prompt_key)}
          primaryButtonName={TEXTS.deleteConfirm.primary}
          secondaryButtonName={TEXTS.deleteConfirm.secondary}
          onPrimaryBtnClick={handleConfirmDelete}
          onClose={() => setPromptToDelete(null)}
        />
      )}
    </div>
  );
};

export default PredefinedPromptsTab;
